import { useRef, useState, useEffect } from "react";
import Map, { NavigationControl, ScaleControl } from "react-map-gl";
import LayersIcon from "../../assets/layer-group-solid.svg?react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import MapboxGLButtonControl from "../../map/MapboxGLButtonControl";
import { debounce } from "lodash";
import { trackEvent, MAP_SEARCH } from "../../utils/googleAnalytics";

import { settings } from "../../config/settings";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "./Map.css";

const styleOptions = [
  {
    label: "Street",
    style: "mapbox://styles/mapbox/standard",
    drawColor: "#1a73e8",
    thickness: 2,
  },
  {
    label: "Hybrid",
    style: "mapbox://styles/mapbox/satellite-streets-v12",
    drawColor: "#00ff88",
    thickness: 3,
  },
  {
    label: "Satellite",
    style: "mapbox://styles/mapbox/satellite-v9",
    drawColor: "#ffff00",
    thickness: 4,
  },
  {
    label: "Terrain",
    style: "mapbox://styles/mapbox/outdoors-v12",
    drawColor: "#ff3b30",
    thickness: 3,
  },
  {
    label: "Dark",
    style: "mapbox://styles/mapbox/dark-v11",
    drawColor: "#00ffff",
    thickness: 2,
  },
  {
    label: "Light",
    style: "mapbox://styles/mapbox/light-v11",
    drawColor: "#5856d6",
    thickness: 2,
  },
];

const MapComponent = ({ onFetchData }) => {
  trackEvent(MAP_SEARCH, "Map Search Page");
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const rectangleControlRef = useRef(null);
  const currentMapStyleRef = useRef(styleOptions[0].style);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);

  const [mapStyle, setMapStyle] = useState(styleOptions[0].style);
  const [drawSettings, setDrawSettings] = useState({
    color: styleOptions[0].drawColor,
    thickness: styleOptions[0].thickness,
  });

  // Initialize rectangle control once
  const initializeRectangleControl = () => {
    if (!rectangleControlRef.current) {
      rectangleControlRef.current = new MapboxGLButtonControl({
        className: "mapbox-gl-draw_rectangle",
        title: "Draw Rectangle",
        eventHandler: drawRectangle,
      });
    }
    return rectangleControlRef.current;
  };

  // Add a resize handler to update the map when sidebar collapses/expands
  useEffect(() => {
    const handleResize = debounce(() => {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        if (map) {
          requestAnimationFrame(() => {
            map.resize();
          });
        }
      }
    }, 100);

    // Add resize event listener
    window.addEventListener("resize", handleResize);

    // Create a mutation observer to watch for class changes on the sidebar
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          requestAnimationFrame(() => {
            handleResize();
          });
        }
      });
    });

    // Find the sidebar element
    const sidebar = document.querySelector(".search-hub-sidebar");
    if (sidebar) {
      observer.observe(sidebar, { attributes: true });
    }

    // Initial resize
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      handleResize.cancel();
    };
  }, []);

  // Update the ref whenever mapStyle changes
  useEffect(() => {
    currentMapStyleRef.current = mapStyle;
  }, [mapStyle]);

  const getDrawStyles = (color, thickness) => [
    // Polygon fill
    {
      id: "gl-draw-polygon-fill",
      type: "fill",
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
      paint: {
        "fill-color": color,
        "fill-outline-color": color,
        "fill-opacity": 0.1,
      },
    },
    // Polygon outline
    {
      id: "gl-draw-polygon-stroke",
      type: "line",
      filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
      paint: {
        "line-color": color,
        "line-width": thickness,
      },
    },
    // Polygon outline when active
    {
      id: "gl-draw-polygon-stroke-active",
      type: "line",
      filter: ["all", ["==", "$type", "Polygon"], ["==", "active", "true"]],
      paint: {
        "line-color": color,
        "line-width": thickness,
      },
    },
    // Vertex points
    {
      id: "gl-draw-polygon-and-line-vertex-active",
      type: "circle",
      filter: [
        "all",
        ["==", "meta", "vertex"],
        ["==", "$type", "Point"],
        ["!=", "mode", "static"],
      ],
      paint: {
        "circle-radius": 5,
        "circle-color": color,
      },
    },
  ];

  function drawRectangle() {
    if (drawRef.current && mapRef.current) {
      drawRef.current.changeMode("draw_rectangle");
      const map = mapRef.current.getMap();
      map.dragPan.disable();
    }
  }

  const handleStyleChange = (option) => {
    setMapStyle(option.style);
    setDrawSettings({ color: option.drawColor, thickness: option.thickness });
    if (drawRef.current && mapRef.current) {
      const map = mapRef.current.getMap();
      // Remove only the draw control
      if (map.hasControl(drawRef.current)) {
        map.removeControl(drawRef.current);
      }

      // Create new draw control with updated styles
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
        },
        styles: getDrawStyles(option.drawColor, option.thickness),
      });

      // Add the draw control
      map.addControl(drawRef.current, "top-left");

      // Reattach event listeners
      map.off("draw.create", handleDrawCreate);
      map.off("draw.update", handleDrawCreate);
      map.on("draw.create", handleDrawCreate);
      map.on("draw.update", handleDrawCreate);

      // Force a map resize to ensure controls are properly positioned
      setTimeout(() => {
        map.resize();
      }, 100);
    }

    setLayerMenuOpen(false); // Close the menu after selection
  };

  // Define a function to handle the 'draw.create' event
  const handleDrawCreate = (e) => {
    const drawnFeature = e.features[0];
    // Use the current value from the ref
    onFetchData(drawnFeature.geometry, currentMapStyleRef.current);
  };

  // Close layer menu when clicking outside
  const handleClickOutside = () => {
    if (layerMenuOpen) {
      setLayerMenuOpen(false);
    }
  };

  // Add event listener for clicks outside the layer menu
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
    // handleClickOutside closes over layerMenuOpen; rebind when that changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerMenuOpen]);

  const handleMapLoad = () => {
    if (!mapRef.current) return; // Wait for the map to initialize
    const map = mapRef.current.getMap();
    // Initialize controls only once
    if (!drawRef.current) {
      // Initialize Mapbox GL Draw
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
        },
        styles: getDrawStyles(drawSettings.color, drawSettings.thickness),
      });

      // Initialize and add rectangle control
      const rectangleControl = initializeRectangleControl();
      map.addControl(rectangleControl, "top-left");
      map.addControl(drawRef.current, "top-left");
    }

    // Remove previous event listeners if they exist
    map.off("draw.create", handleDrawCreate);
    map.off("draw.update", handleDrawCreate);

    // Add new event listeners
    map.on("draw.create", handleDrawCreate);
    map.on("draw.update", handleDrawCreate);
    map.on("draw.modechange", (e) => {
      const mode = e.mode;
      if (mode === "draw_polygon") {
        map.dragPan.disable();
      } else {
        map.dragPan.enable();
      }
    });
  };

  return (
    <div className="map-content-wrapper">
      <div className="map-container">
        <Map
          mapLib={import("mapbox-gl")}
          ref={mapRef}
          initialViewState={{
            longitude: -99.9018,
            latitude: 31.9686,
            zoom: 6,
          }}
          style={{ height: "100%", width: "100%" }}
          mapStyle={mapStyle}
          mapboxAccessToken={settings.mapboxKey}
          onLoad={handleMapLoad}
        >
          <NavigationControl position="top-left" />
          <ScaleControl position="bottom-left" unit="imperial" />

          {/* Layer Switcher Button */}
          <div className="layer-switcher">
            <LayersIcon className="layer-icon" />
            <div className="layer-dropdown">
              {styleOptions.map((option) => (
                <div
                  key={option.label}
                  className="layer-option"
                  onClick={() => handleStyleChange(option)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleStyleChange(option);
                    }
                  }}
                  role="radio"
                  tabIndex={0}
                  aria-checked={mapStyle === option.style}
                >
                  <input
                    type="radio"
                    name="mapStyle"
                    checked={mapStyle === option.style}
                    readOnly
                  />
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        </Map>
      </div>
      <div className="map-instructions">
        <h3>Draw a polygon on the map to search for data in that area</h3>
        <p>
          Use the rectangle or polygon tool in the top-left corner to select an
          area of interest.
        </p>
        <p>
          Change the map style by hovering over the layers icon{" "}
          <span role="img" aria-label="layers">
            📚
          </span>{" "}
          in the top-right corner and selecting your preferred view (Street,
          Satellite, Terrain, etc.).
        </p>
      </div>
    </div>
  );
};

export default MapComponent;
